// useAuditLog.ts — Fetches the admin audit log with filter + pagination.
//
// Calls admin_get_audit_log_filtered(actionType, sinceTimestamp, limit).
// The Motoko method returns entries newest-last; we reverse for newest-first
// display as specified (AU-03: "Sorted newest first").
//
// CSV export is a pure client-side operation — no second network call.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Identity } from '@dfinity/agent';
import { createAdminAgent, createAdminActor, type AdminAction } from '../lib/adminAgent';

export const AUDIT_ACTION_TYPES = [
  'all',
  'cycle_topup',
  'auto_top_up_deposit',
  'auto_top_up_cap_hit',
  'auto_top_up_skip',
  'set_auto_top_up_enabled',
  'set_auto_top_up_threshold',
  'set_auto_top_up_amount',
  'set_daily_top_up_cap',
  'admin_add_banned_word',
  'admin_remove_banned_word',
  'add_admin',
  'remove_admin',
  'init_admin',
  'force_rename',
  'force_finish_room',
] as const;

export type AuditActionFilter = (typeof AUDIT_ACTION_TYPES)[number];

export interface AuditLogFilters {
  actionType: AuditActionFilter;
  sinceTimestamp: bigint | null;  // nanoseconds (ICP time)
  limit: number;
}

export const DEFAULT_FILTERS: AuditLogFilters = {
  actionType: 'all',
  sinceTimestamp: null,
  limit: 50,
};

export interface UseAuditLogReturn {
  entries: AdminAction[];
  isLoading: boolean;
  error: string | null;
  filters: AuditLogFilters;
  setFilters: (f: Partial<AuditLogFilters>) => void;
  refresh: () => void;
  exportCsv: () => void;
}

// Format a nanosecond ICP timestamp as a human-readable date string.
function formatTimestamp(ns: bigint): string {
  const ms = Number(ns / 1_000_000n);
  return new Date(ms).toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
}

function buildCsvRow(entry: AdminAction): string {
  const ts = formatTimestamp(entry.timestamp);
  const caller = entry.caller.toText();
  const actionType = entry.actionType;
  const target = entry.targetPrincipal.length > 0 ? entry.targetPrincipal[0]!.toText() : '';
  const details = entry.details.replace(/"/g, '""');
  return `"${ts}","${caller}","${actionType}","${target}","${details}"`;
}

export function useAuditLog(identity: Identity | null): UseAuditLogReturn {
  const [entries, setEntries] = useState<AdminAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<AuditLogFilters>(DEFAULT_FILTERS);

  const isMountedRef = useRef(true);

  const fetchLog = useCallback(async (currentFilters: AuditLogFilters) => {
    if (!identity) return;
    setIsLoading(true);
    setError(null);
    try {
      const agent = await createAdminAgent(identity);
      const actor = createAdminActor(agent);

      const actionTypeArg: [] | [string] =
        currentFilters.actionType === 'all' ? [] : [currentFilters.actionType];
      const sinceArg: [] | [bigint] =
        currentFilters.sinceTimestamp !== null ? [currentFilters.sinceTimestamp] : [];

      const result = await actor.admin_get_audit_log_filtered(
        actionTypeArg,
        sinceArg,
        BigInt(currentFilters.limit),
      );

      if (!isMountedRef.current) return;

      if ('err' in result) {
        setError(result.err);
      } else {
        // Motoko returns newest-last; reverse for newest-first display.
        setEntries([...result.ok].reverse());
      }
    } catch (err) {
      if (isMountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to fetch audit log.';
        setError(msg);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [identity]);

  const refresh = useCallback(() => { fetchLog(filters); }, [fetchLog, filters]);

  const setFilters = useCallback((partial: Partial<AuditLogFilters>) => {
    setFiltersState((prev) => {
      const next = { ...prev, ...partial };
      fetchLog(next);
      return next;
    });
  }, [fetchLog]);

  useEffect(() => {
    if (!identity) return;
    isMountedRef.current = true;
    fetchLog(filters);
    return () => { isMountedRef.current = false; };
    // Only re-fetch when identity changes. Filter changes handled by setFilters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);

  const exportCsv = useCallback(() => {
    const header = '"Timestamp","Caller","Action","Target","Details"';
    const rows = entries.map(buildCsvRow);
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    link.setAttribute('href', url);
    link.setAttribute('download', `dominus-audit-${ts}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [entries]);

  return { entries, isLoading, error, filters, setFilters, refresh, exportCsv };
}
